<script setup lang="ts">
import { computed } from 'vue'

import { Topic, TopicLabels, type TopicId } from '@/shared/constants'
import { Tooltip } from '@/shared/ui/Tooltip'

/**
 * The website topic as a glyph — the mark a list uses where a column of nine words
 * would not fit. It carries its own tooltip, because an icon alone names nothing.
 *
 * The drawings are inline rather than files: there are nine of them, they are one
 * `stroke="currentColor"` path set each, and the build has no svg-to-component step.
 */
const props = withDefaults(
  defineProps<{
    /** A `Topic` id. A row without one renders nothing at all. */
    topic?: number | null
    size?: number
  }>(),
  { topic: null, size: 16 }
)

const label = computed(() =>
  props.topic == null ? '' : (TopicLabels[props.topic as TopicId] ?? '')
)
</script>

<template>
  <Tooltip v-if="label" :content="label" placement="top">
    <svg
      class="topic-icon"
      :width="size"
      :height="size"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      role="img"
      :aria-label="label"
    >
      <template v-if="topic === Topic.Straight">
        <circle cx="8.5" cy="13.5" r="3.8" />
        <path d="M8.5 17.3v4M6.2 19.3h4.6" />
        <circle cx="15" cy="10" r="3.8" />
        <path d="m17.7 7.3 3.3-3.3M17 4h4v4" />
      </template>

      <template v-else-if="topic === Topic.Gay">
        <circle cx="8" cy="15.5" r="4.5" />
        <circle cx="14.5" cy="15.5" r="4.5" />
        <path d="M4.8 12.3 2.8 10.3M6 10H2.5v3.5" />
        <path d="m17.7 12.3 1.9-1.9M16 10h3.5v3.5" />
      </template>

      <template v-else-if="topic === Topic.Trans">
        <circle cx="12" cy="12.8" r="3.8" />
        <path d="M12 16.6v4.4M9.7 19.2h4.6" />
        <path d="m14.7 10.1 3.3-3.3M14 6.8h4v4" />
        <path d="M9.3 10.1 6 6.8m-.4 2.2 2.6-2.6" />
      </template>

      <template v-else-if="topic === Topic.AdultToons">
        <circle cx="11" cy="13" r="7.5" />
        <path d="M7.9 15.4c1.7 2.3 4.5 2.3 6.2 0" />
        <path
          d="M20 2.2c.3 1.4.9 2 2.3 2.3-1.4.3-2 .9-2.3 2.3-.3-1.4-.9-2-2.3-2.3 1.4-.3 2-.9 2.3-2.3z"
        />
        <circle cx="8.3" cy="11.3" r="1.05" fill="currentColor" stroke="none" />
        <circle
          cx="13.7"
          cy="11.3"
          r="1.05"
          fill="currentColor"
          stroke="none"
        />
      </template>

      <template v-else-if="topic === Topic.Entertainment">
        <path d="M13 5.5H4.2v5.2c0 3.2 1.9 5.9 4.4 6.4" />
        <path
          d="M9.8 8.2h10.5v5.5c0 3.5-2.35 6.3-5.25 6.3S9.8 17.2 9.8 13.7V8.2z"
        />
        <path d="M12.8 15.6c1 1.3 3.4 1.3 4.4 0" />
        <circle cx="12.6" cy="11.9" r=".95" fill="currentColor" stroke="none" />
        <circle cx="17.5" cy="11.9" r=".95" fill="currentColor" stroke="none" />
      </template>

      <template v-else-if="topic === Topic.FileSharing">
        <circle cx="18" cy="5.5" r="2.2" />
        <circle cx="18" cy="18.5" r="2.2" />
        <circle cx="6" cy="12" r="2.2" />
        <path d="m7.9 10.9 8.2-4.3M7.9 13.1l8.2 4.3" />
      </template>

      <template v-else-if="topic === Topic.Finance">
        <rect x="2.5" y="6" width="19" height="12" rx="2" />
        <circle cx="12" cy="12" r="2.8" />
        <circle cx="6" cy="12" r=".9" fill="currentColor" stroke="none" />
        <circle cx="18" cy="12" r=".9" fill="currentColor" stroke="none" />
      </template>

      <template v-else-if="topic === Topic.VideoHosting">
        <path
          d="M6.5 7.5V6a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-1.2"
        />
        <rect x="2.5" y="8" width="15" height="12" rx="2.5" />
        <path d="m8.2 11.2 5.6 2.8-5.6 2.8z" />
      </template>

      <template v-else-if="topic === Topic.Other">
        <circle cx="12" cy="12" r="9" />
        <circle cx="8" cy="12" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="16" cy="12" r="1.15" fill="currentColor" stroke="none" />
      </template>
    </svg>
  </Tooltip>
</template>

<style lang="scss" scoped>
.topic-icon {
  display: block;
  flex-shrink: 0;
  color: var(--el-text-color-secondary);
}
</style>
